/**
 * Canonical analytics payload builders (Milestone 15).
 */
import { toListingAnalyticsPayload } from "../utils/automapListing/listingViewModels";
import { parseVehicleSearchParams } from "../utils/vehicleSearch/vehicleSearchParamsUrl";
import { getFairPriceViewModel, resolveFairPriceLabel } from "../utils/fairPriceViewModel";
import { getVehicleRiskViewModel, resolveRiskLevel } from "../utils/vehicleRiskViewModel";
import { getDealerTrustViewModel } from "../utils/dealerTrustViewModel";
import { buildAttributionContext } from "./attribution";
import { buildDealerAnalyticsPayload as buildProfileDealerAnalyticsPayload } from "../utils/dealerProfile";
import { sanitizeAnalyticsPayload } from "./sanitize";

export function buildListingAnalyticsPayload(listing, extra = {}) {
  if (!listing) return sanitizeAnalyticsPayload({ sourcePage: extra.sourcePage, ...extra });
  return sanitizeAnalyticsPayload(
    toListingAnalyticsPayload(listing, {
      listingSlug: listing.slug || listing.id || null,
      dealerName: listing.dealerName || null,
      mileageKm: listing.mileageKm ?? null,
      availabilityStatus: listing.availabilityStatus || null,
      listingStatus: listing.status || null,
      ...extra,
    })
  );
}

export function buildDealerAnalyticsPayload(dealer, extra = {}) {
  if (!dealer) return sanitizeAnalyticsPayload(extra);
  return sanitizeAnalyticsPayload(buildProfileDealerAnalyticsPayload(dealer, extra));
}

export function buildSearchAnalyticsPayload(searchParams, resultContext = {}) {
  const p = parseVehicleSearchParams(searchParams);
  const pagination = resultContext.pagination || {};
  const items = resultContext.items || [];

  return sanitizeAnalyticsPayload({
    sourcePage: "search_results",
    route: resultContext.route || null,
    filters: p,
    sort: p.sort,
    query: p.keyword || null,
    page: p.page,
    pageSize: p.pageSize,
    resultCount: pagination.totalCount ?? pagination.total ?? resultContext.resultCount ?? items.length,
    visibleResultCount: resultContext.visibleResultCount ?? items.length,
    city: p.city,
    make: p.make,
    model: p.model,
    bodyType: p.bodyType,
    transmission: p.transmission,
    priceMinCop: p.priceMinCop,
    priceMaxCop: p.priceMaxCop,
    yearMin: p.yearMin,
    yearMax: p.yearMax,
  });
}

export function buildFairPriceAnalyticsPayload(listing, extra = {}) {
  if (!listing) return sanitizeAnalyticsPayload(extra);
  const vm = getFairPriceViewModel(listing);
  return buildListingAnalyticsPayload(listing, {
    fairPriceLabel: vm.label || resolveFairPriceLabel(listing),
    fairPriceDeltaCop: listing.fairPriceDeltaCop ?? null,
    fairPriceConfidence: listing.fairPriceConfidence ?? null,
    fairPriceSampleSize: vm.sampleSize ?? null,
    sourcePage: extra.sourcePage || "fair_price",
    ...extra,
  });
}

export function buildRiskAnalyticsPayload(listing, extra = {}) {
  if (!listing) return sanitizeAnalyticsPayload(extra);
  const vm = getVehicleRiskViewModel(listing);
  return buildListingAnalyticsPayload(listing, {
    riskLevel: vm.level || resolveRiskLevel(listing),
    riskFlags: (vm.flags || []).map((flag) => flag?.key || flag).filter(Boolean),
    qualityScore: listing.qualityScore ?? null,
    sourcePage: extra.sourcePage || "vehicle_risk",
    ...extra,
  });
}

export function buildDealerContactAnalyticsPayload(listing, dealer = null, extra = {}) {
  const trust = listing ? getDealerTrustViewModel(listing, dealer, { slug: extra.slug }) : null;
  const listingPayload = listing ? buildListingAnalyticsPayload(listing, extra) : sanitizeAnalyticsPayload(extra);

  return sanitizeAnalyticsPayload({
    ...listingPayload,
    dealerId: trust?.dealerId || listing?.dealerId || extra.dealerId || null,
    dealerName: trust?.dealerName || listing?.dealerName || null,
    dealerVerified: trust?.isVerified ?? Boolean(listing?.dealerVerified),
    dealerVerificationStatus: trust?.verificationStatus || listing?.dealerVerificationStatus || null,
    contactMethod: extra.contactMethod || null,
    sourceAction: extra.sourceAction || null,
    attributionContext: extra.attributionContext || buildAttributionContext(),
  });
}

export function buildRecommendationAnalyticsPayload(sourceListing, recommendedListing, extra = {}) {
  return sanitizeAnalyticsPayload({
    sourceListingId: sourceListing?.id || null,
    sourceListingSlug: sourceListing?.slug || sourceListing?.id || null,
    recommendedListingId: recommendedListing?.id || null,
    recommendedListingSlug: recommendedListing?.slug || recommendedListing?.id || null,
    dealerId: recommendedListing?.dealerId || sourceListing?.dealerId || null,
    city: recommendedListing?.city || sourceListing?.city || null,
    make: recommendedListing?.make || sourceListing?.make || null,
    model: recommendedListing?.model || sourceListing?.model || null,
    bodyType: recommendedListing?.bodyType || sourceListing?.bodyType || null,
    priceCop: recommendedListing?.priceCop ?? sourceListing?.priceCop ?? null,
    sourcePage: extra.sourcePage || "vehicle_detail",
    sourceComponent: extra.sourceComponent || "vehicle_recommendation_rail",
    ...extra,
  });
}

export function buildChecklistAnalyticsPayload(listing, item = null, extra = {}) {
  const base = listing ? buildListingAnalyticsPayload(listing, extra) : sanitizeAnalyticsPayload(extra);
  return sanitizeAnalyticsPayload({
    ...base,
    checklistItemKey: item?.id || item?.key || extra.checklistItemKey || null,
    checklistCategory: item?.category || extra.checklistCategory || null,
    checklistPriority: item?.priority || extra.checklistPriority || null,
  });
}

export function buildLeadAnalyticsPayload(lead = {}, extra = {}) {
  return sanitizeAnalyticsPayload({
    leadId: lead.id || lead.leadId || extra.leadId || null,
    leadStatus: lead.status || extra.leadStatus || null,
    listingId: lead.listingId || extra.listingId || null,
    dealerId: lead.dealerId || extra.dealerId || null,
    contactMethod: lead.contactMethod || extra.contactMethod || null,
    sourcePage: extra.sourcePage || null,
    sourceComponent: extra.sourceComponent || "lead_capture",
    sourceAction: extra.sourceAction || null,
    attributionContext: extra.attributionContext || buildAttributionContext(),
    ...extra,
  });
}
